import {
  eventPlace,
  formatTimeRange,
  onlineLabel,
} from "../../helpers/calendar";
import { colors } from "../../style";
import { Avatar, Btn, Chip, Dialog, IconButton, Icon, ui } from "../ui";
import { eventColor, eventTitle } from "./common";

const ellipsis = {
  display: "block",
  overflow: "hidden",
  textOverflow: "ellipsis",
  whiteSpace: "nowrap",
};

const RESPONSES = {
  organizer: { label: "Organiser", color: colors.violet },
  accepted: { label: "Accepted", color: colors.green },
  tentativelyAccepted: { label: "Tentative", color: colors.amber },
  tentative: { label: "Tentative", color: colors.amber },
  declined: { label: "Declined", color: colors.red },
  none: { label: "Not responded", color: colors.textFaint },
  notResponded: { label: "Not responded", color: colors.textFaint },
  needsAction: { label: "Not responded", color: colors.textFaint },
};

const PROVIDER_APP = { google: "Google Calendar", microsoft: "Outlook" };

function duration(minutes) {
  const h = Math.floor(minutes / 60);
  const m = minutes % 60;
  return [h && `${h} hr`, m && `${m} min`].filter(Boolean).join(" ");
}

function CalendarEventDetails({ event, now, onClose }) {
  if (!event) return null;
  const ended = event.end.isBefore(now);
  const place = eventPlace(event);
  const online = onlineLabel(event);
  const people = [
    {
      name: event.organiser?.name,
      email: event.organiser?.email,
      response: "organizer",
    },
    ...(event.attendees ?? []),
  ].filter((p) => p.name || p.email);

  return (
    <Dialog open onClose={onClose} label={eventTitle(event)} width={480}>
      <div
        style={{
          padding: "20px",
          display: "flex",
          flexDirection: "column",
          gap: "12px",
        }}
      >
        <div style={{ display: "flex", alignItems: "flex-start", gap: "8px" }}>
          <Chip
            color={eventColor(event)}
            icon={event.is_online ? "video" : "users"}
          >
            {online && online !== "Online"
              ? `Online · ${online}`
              : online || "In person"}
          </Chip>
          <span style={{ flex: 1 }} />
          <IconButton icon="close" label="Close" onClick={onClose} />
        </div>

        <h2 style={{ ...ui.sectionTitle, fontSize: "18px", lineHeight: 1.3 }}>
          {eventTitle(event)}
        </h2>

        <div style={{ ...ui.muted, fontSize: "14px", lineHeight: 1.5 }}>
          {event.start.format("dddd D MMMM YYYY")}
          <br />
          {formatTimeRange(event)}
          {!event.is_all_day &&
            event.duration_minutes > 0 &&
            ` · ${duration(event.duration_minutes)}`}
        </div>

        {place && (
          <div
            style={{
              ...ui.muted,
              fontSize: "13px",
              display: "flex",
              gap: "6px",
              alignItems: "flex-start",
            }}
          >
            <Icon name="mapPin" size={14} style={{ marginTop: 2 }} />
            {place}
          </div>
        )}

        {people.length > 0 && (
          <div
            style={{
              display: "flex",
              flexDirection: "column",
              gap: "6px",
              maxHeight: "240px",
              overflowY: "auto",
              paddingTop: "4px",
            }}
          >
            {people.map((p) => {
              const r = RESPONSES[p.response];
              return (
                <div
                  key={p.email || p.name}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    fontSize: "13px",
                  }}
                >
                  <Avatar name={p.name || p.email} size={24} />
                  <span
                    title={p.email}
                    style={{ ...ellipsis, flex: 1, minWidth: 0 }}
                  >
                    {p.name || p.email}
                  </span>
                  {r && (
                    <span style={{ fontSize: "12px", color: r.color }}>
                      {r.label}
                    </span>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <div
          style={{
            display: "flex",
            flexWrap: "wrap",
            gap: "8px",
            marginTop: "6px",
          }}
        >
          {!ended && event.join_url && (
            <Btn
              as="a"
              href={event.join_url}
              target="_blank"
              rel="noopener noreferrer"
              variant="primary"
              icon="video"
            >
              Join
            </Btn>
          )}
          {event.web_link && (
            <Btn
              as="a"
              href={event.web_link}
              target="_blank"
              rel="noopener noreferrer"
              icon="externalLink"
            >
              Open in {PROVIDER_APP[event.provider] ?? "calendar"}
            </Btn>
          )}
        </div>
      </div>
    </Dialog>
  );
}

export default CalendarEventDetails;
